import { components } from "@/lib/openapi";
import { client } from "@/queries/client";
import { ExploreConfig } from "@/types/explore";
import { useMutation, useQuery } from "@tanstack/react-query";

export type Report = components["schemas"]["ReportSchema"];
export type Summary = components["schemas"]["SummarySchema"];
export type CanceledSummary = components["schemas"]["CanceledSummarySchema"];
type ReportMetadata = {
  latest_report_id: number;
  updated_at: string;
  status: string;
  target_date?: string | null;
};

export function getReport({ report = undefined, enabled = true } = {}) {
  return useQuery({
    enabled: Boolean(enabled && report != null),
    queryKey: ["reports", report],
    retry: (failureCount, error: any) => {
      // Don't retry on 404 (report doesn't exist or still building)
      if (error?.response?.status === 404) return false;
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    queryFn: async ({ signal }) => {
      if (report == null) throw new Error("Report ID is required");
      const { data, error, response } = await client
        .get()
        .GET("/api/report/{report_id}", {
          signal,
          params: {
            path: {
              report_id: report,
            },
          },
        });
      if (data) return data;
      console.warn(error);
      // Throw error with response for retry logic
      const err: any = new Error(`Failed to fetch report ${report}`);
      err.response = response;
      throw err;
    },
  });
}

export function getAllReports() {
  return useQuery({
    queryKey: ["all_reports"],
    queryFn: async ({ signal }) => {
      // @ts-ignore
      const { data, error } = await client.get().GET("/api/report", {
        signal,
      });
      if (data) return data;
      console.warn(error);
      throw new Error();
    },
  });
}

export function getLatestReport() {
  return useQuery<Report | null>({
    queryKey: ["latest_report"],
    // Keep the dashboard bound to the newest report without requiring a manual reload.
    // Poll every minute, continue while the tab is backgrounded, and catch up on focus.
    refetchInterval: 60 * 1000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 1;
    },
    queryFn: async ({ signal }) => {
      // @ts-ignore
      const { data, error, response } = await client
        .get()
        .GET("/api/report/latest", {
          signal,
        });
      if (data) return data;
      if (response?.status === 404) return null;
      console.warn(error);
      const err: any = new Error("Failed to fetch latest report");
      err.response = response;
      throw err;
    },
  });
}

export function getReportComponentData({
  report,
  component,
}: {
  report: number;
  component: number;
}) {
  return useQuery({
    queryKey: ["reports", report, "components", component],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .GET("/api/report/{report_id}/data/{component_id}", {
          signal,
          params: {
            path: {
              report_id: report,
              component_id: component,
            },
          },
        });
      if (data) return data;
      console.warn(error);
      throw new Error();
    },
  });
}

export function buildReport() {
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await client.get().POST("/api/report", {});
      if (data) return data;
      console.warn(error);
      throw new Error();
    },
  });
}

export function getZoneData(
  config: ExploreConfig,
  report: number,
  enabled: boolean = true
) {
  return useQuery({
    enabled,
    queryKey: ["zone_data", report, config],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .POST("/api/report/zone_data/{report_id}/", {
          signal,
          params: {
            path: {
              report_id: report.toString(),
            },
          },
          body: {
            config,
          },
        });

      if (data) return data;
      console.warn(error);
      throw new Error();
    },
  });
}

export type DataRequestSchema = components["schemas"]["DataRequestSchema"][];
export type ZoneQuery = components["schemas"]["ZoneQuery"];

export function getAggregateData(
  config: DataRequestSchema,
  report: number,
  enabled: boolean = true
) {
  return useQuery({
    enabled,
    queryKey: ["aggregate_data", report, config],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .POST("/api/report/{report_id}/aggregate", {
          signal,
          params: {
            path: {
              report_id: report,
            },
          },
          body: config,
        });

      if (data) return data;
      console.warn(error);
      throw new Error();
    },
  });
}

export type WaitTimeRequestSchema =
  components["schemas"]["WaitTimeRequestSchema"];

export function getWaitTimeData(
  config: WaitTimeRequestSchema,
  report: number,
  enabled: boolean = true
) {
  return useQuery({
    enabled,
    queryKey: ["wait_times", report, config],
    queryFn: async ({ signal }) => {
      const { data, error } = await client
        .get()
        .POST("/api/report/{report_id}/wait", {
          signal,
          params: {
            path: {
              report_id: report,
            },
          },
          body: config,
        });

      if (data) return data;
      console.warn(error);
      throw new Error();
    },
  });
}

export function getPollingReport(reportId: number | null, enabled = false) {
  return useQuery({
    enabled: enabled && reportId !== null,
    queryKey: ["report-polling", reportId],
    queryFn: async ({ signal }) => {
      if (!reportId) throw new Error("No report ID provided");

      // Use metadata endpoint to avoid 404 errors while report is building
      const { data, error } = await client
        .get()
        // @ts-expect-error - Metadata endpoint not yet in OpenAPI spec
        .GET("/api/report/latest/metadata", { signal });

      if (data) {
        const metadata = data as ReportMetadata;
        return {
          id: metadata.latest_report_id,
          status: metadata.status,
          target_date: metadata.target_date,
          updated_at: metadata.updated_at,
        };
      }
      console.warn(error);
      throw new Error();
    },
    refetchInterval: enabled ? 10000 : false,
  });
}

export function getZoneContributions(
  zoneId: number | null,
  interval: number | null,
  zoneIds?: number[] | null
) {
  return useQuery({
    enabled: zoneId !== null && interval !== null,
    queryKey: ["zone_contributions", zoneId, interval, zoneIds],
    queryFn: async ({ signal }) => {
      if (zoneId === null || interval === null)
        throw new Error("Missing parameters");

      const { data, error } = await client
        .get()
        .GET("/api/zones/{zone_id}/contributions/{interval}", {
          signal,
          params: {
            path: {
              zone_id: zoneId,
              interval: interval,
            },
            query:
              zoneIds && zoneIds.length > 0
                ? {
                    zone_ids: zoneIds.join(","),
                  }
                : undefined,
          },
        });

      if (data) return data;
      console.warn(error);
      throw new Error();
    },
    staleTime: Infinity,
  });
}
